import React, { Suspense } from 'react';
import { Navigate, useLocation, useParams, useRoutes } from 'react-router-dom';
import DashboardPage from './features/dashboard/DashboardPage';
import StatsPage from './features/stats/StatsPage';
import ActivityDetailPage from './features/stats/ActivityDetailPage';
import SettingsPage from './features/settings/SettingsPage';
import SubscriptionPage from './features/subscription/SubscriptionPage';
import AuthComponent from './features/auth/AuthComponent';
import VerifyEmailPage from './features/auth/VerifyEmailPage';
import ForgotPasswordPage from './features/auth/ForgotPasswordPage';
import ResetPasswordPage from './features/auth/ResetPasswordPage';
import LoginFailed from './features/auth/LoginFailed';
import LoginSuccessRedirect from './features/auth/LoginSuccessRedirect';
import ContactPage from './features/contact/ContactPage';
import SeoHead from './features/seo/SeoHead';
import RouteLoadFallback from './features/common/RouteLoadFallback';
import LazyRouteErrorBoundary from './features/common/LazyRouteErrorBoundary';
import { getRouteSeo } from './features/seo/seoConfig';
import { legacyGamesBrowseRedirect } from './features/games/shared/gamesBrowseUrl';

const PuzzlesPage = React.lazy(() => import('./features/puzzles/PuzzlesPage'));
const PuzzleByIdPage = React.lazy(() => import('./features/puzzles/PuzzleByIdPage'));
const ReviewHubPage = React.lazy(() => import('./features/review/ReviewHubPage'));
const ReviewPage = React.lazy(() => import('./features/review/ReviewPage'));
const ReviewManagePage = React.lazy(() => import('./features/review/ReviewManagePage'));
const ReviewManageCoursePage = React.lazy(
  () => import('./features/review/ReviewManageCoursePage'),
);
const GamesHubPage = React.lazy(() => import('./features/games/GamesHubPage'));
const GamesBrowsePage = React.lazy(() => import('./features/games/browse/GamesBrowsePage'));
const MyGamesPage = React.lazy(() => import('./features/games/my-games/MyGamesPage'));
const MyGameTrainerPage = React.lazy(
  () => import('./features/games/my-games/MyGameTrainerPage'),
);
const WhiteRepertoireCollectionPage = React.lazy(
  () => import('./features/courses/WhiteRepertoireCollectionPage'),
);
const BlackBrowseFamilyPage = React.lazy(
  () => import('./features/courses/BlackBrowseFamilyPage'),
);
const OpeningSidePage = React.lazy(
  () => import('./features/courses/OpeningSidePage'),
);
const CourseTypesPage = React.lazy(() => import('./features/courses/CourseTypesPage'));
const CourseListPage = React.lazy(() => import('./features/courses/CourseListPage'));
const CourseDetailPage = React.lazy(() => import('./features/courses/CourseDetailPage'));
const CourseLessonPage = React.lazy(() => import('./features/courses/CourseLessonPage'));
const CourseTrainAllPage = React.lazy(() => import('./features/courses/CourseTrainAllPage'));
const ExplorerBrowsePage = React.lazy(() => import('./features/explorer/ExplorerBrowsePage'));
const ExplorerOpeningsHubPage = React.lazy(
  () => import('./features/explorer/ExplorerOpeningsHubPage'),
);
const ExplorerOpeningPage = React.lazy(
  () => import('./features/explorer/ExplorerOpeningPage'),
);
const ExplorerPage = React.lazy(() => import('./features/explorer/ExplorerPage'));
const ReplayPage = React.lazy(() => import('./features/replay/ReplayPage'));

const DashboardKindRedirect = () => {
  const { kind } = useParams();
  const { search } = useLocation();
  return <Navigate to={`/stats/${kind}${search}`} replace />;
};

const LegacyGamesRedirect = () => {
  const { search } = useLocation();
  const target = legacyGamesBrowseRedirect(search);
  if (target) {
    return <Navigate to={target} replace />;
  }
  return <GamesHubPage />;
};

const RouteSeo = () => {
  const { pathname, search } = useLocation();
  const seo = getRouteSeo(pathname, search);

  return (
    <SeoHead
      title={seo.title}
      description={seo.description}
      canonicalPath={seo.canonicalPath}
      noindex={seo.noindex}
      jsonLd={seo.jsonLd}
    />
  );
};

const AppRoutes = () => {
  const routes = [
    {
      path: '/',
      element: <AuthComponent />,
    },
    {
      path: '/puzzles',
      element: <PuzzlesPage />,
    },
    {
      path: '/hard-puzzles',
      element: <Navigate to="/puzzles" replace />,
    },
    { path: '/puzzles/by-id/:puzzleId', element: <PuzzleByIdPage /> },
    { path: '/review', element: <ReviewHubPage /> },
    { path: '/review/manage', element: <ReviewManagePage /> },
    { path: '/review/manage/course/:courseSlug', element: <ReviewManageCoursePage /> },
    { path: '/review/:category', element: <ReviewPage /> },
    { path: '/dashboard', element: <DashboardPage /> },
    { path: '/dashboard/:kind', element: <DashboardKindRedirect /> },
    { path: '/stats', element: <StatsPage /> },
    { path: '/stats/:kind', element: <ActivityDetailPage /> },
    { path: '/settings', element: <SettingsPage /> },
    { path: '/contact', element: <ContactPage /> },
    { path: '/subscribe', element: <SubscriptionPage /> },
    { path: '/games', element: <LegacyGamesRedirect /> },
    {
      path: '/games/browse/openings/:familySlug/:variationSlug/:eco',
      element: <GamesBrowsePage />,
    },
    {
      path: '/games/browse/openings/:familySlug/:variationSlug',
      element: <GamesBrowsePage />,
    },
    {
      path: '/games/browse/openings/:familySlug',
      element: <GamesBrowsePage />,
    },
    {
      path: '/games/browse/:corpus/:playerSlug/openings/:familySlug/:variationSlug/:eco',
      element: <GamesBrowsePage />,
    },
    {
      path: '/games/browse/:corpus/:playerSlug/openings/:familySlug/:variationSlug',
      element: <GamesBrowsePage />,
    },
    {
      path: '/games/browse/:corpus/:playerSlug/openings/:familySlug',
      element: <GamesBrowsePage />,
    },
    { path: '/games/browse/:corpus/:playerSlug', element: <GamesBrowsePage /> },
    { path: '/games/browse', element: <GamesBrowsePage /> },
    { path: '/games/mine', element: <MyGamesPage /> },
    { path: '/games/mine/:gameId', element: <MyGameTrainerPage /> },
    { path: '/courses', element: <CourseTypesPage /> },
    { path: '/courses/train', element: <CourseTrainAllPage /> },
    { path: '/courses/opening/w', element: <OpeningSidePage /> },
    { path: '/courses/opening/w/:collection/train', element: <CourseTrainAllPage /> },
    { path: '/courses/opening/w/:collection', element: <WhiteRepertoireCollectionPage /> },
    { path: '/courses/opening/b/train', element: <CourseTrainAllPage /> },
    { path: '/courses/opening/b', element: <OpeningSidePage /> },
    { path: '/courses/opening/b/:familySlug/train', element: <CourseTrainAllPage /> },
    { path: '/courses/opening/b/:familySlug', element: <BlackBrowseFamilyPage /> },
    { path: '/courses/:phase/train', element: <CourseTrainAllPage /> },
    { path: '/courses/:phase', element: <CourseListPage /> },
    {
      path: '/courses/:phase/:courseSlug/lessons/:lessonId',
      element: <CourseLessonPage />,
    },
    { path: '/courses/:phase/:courseSlug/train', element: <CourseTrainAllPage /> },
    { path: '/courses/:phase/:courseSlug', element: <CourseDetailPage /> },
    {
      path: '/top-games',
      element: <Navigate to="/games/browse" replace />,
    },
    { path: '/explorer/browse', element: <ExplorerBrowsePage /> },
    { path: '/explorer/openings', element: <ExplorerOpeningsHubPage /> },
    {
      path: '/explorer/openings/:eco/:slug',
      element: <ExplorerOpeningPage />,
    },
    { path: '/explorer/*', element: <ExplorerPage /> },
    { path: '/replay/browse', element: <ReplayPage /> },
    { path: '/replay/history', element: <ReplayPage /> },
    { path: '/replay', element: <ReplayPage /> },
    { path: '/replay/:gameId', element: <ReplayPage /> },
    {
      path: '/import',
      element: <Navigate to="/games/mine" replace />,
    },
    {
      path: '/login',
      element: <AuthComponent />,
    },
    { path: '/verify-email', element: <VerifyEmailPage /> },
    { path: '/forgot-password', element: <ForgotPasswordPage /> },
    { path: '/reset-password', element: <ResetPasswordPage /> },
    {
      path: '/login-failed',
      element: <LoginFailed />,
    },
    {
      path: '/login-success',
      element: <LoginSuccessRedirect />,
    },
  ];

  return (
    <>
      <RouteSeo />
      <LazyRouteErrorBoundary>
        <Suspense fallback={<RouteLoadFallback />}>
          {useRoutes(routes)}
        </Suspense>
      </LazyRouteErrorBoundary>
    </>
  );
};

export default AppRoutes;
